import React, { useState } from "react";
import GuideSection from "../../components/guide/GuideSection";
import { paymentGuide } from "../../components/guide/guideContent";
import { formatPrice } from "../../utils/formatPrice";
import { Button, Form } from "react-bootstrap";
import FormalityHeader from "../../components/header/FormalityHeader";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  cardPayment,
  checkPayment,
  mobilePayment,
  setPaymentFrom,
} from "../../redux/actions/PaymentActions";
import PhoneInput from "react-phone-input-2";
import { FiLock } from "react-icons/fi";
import "../styles/cart.css";
import Loader from "../../components/loader/Loader";
import CardPaymentMethod, {
  CARD,
} from "../../components/payment/CardPaymentMethod";
import useCardOffer from "../../hooks/useCardOffer";
import { openCardCheckout } from "../../utils/cardCheckout";

const FORMALITY_NAMES = {
  crimRecord: "Extrait de casier judiciaire",
  certificateNationality: "Certificat de nationalité",
  certificateSingleness: "Certificat de célibat",
  eneo: "Branchement ENEO",
  customsRebate: "Abattement douanier",
  businessPlan: "Business plan",
  importation: "Inscription au fichier des importateurs",
  oapi: "Protection du nom commercial à l'OAPI",
  passport: "Pré-enrôlement Passeport",
  visualIdentity: "Identité visuelle",
  trademark: "Protection de la marque à l'OAPI",
  taxRegistration: "Attestation d'immatriculation (NIU)",
  taxCompliance: "Attestation de conformité fiscale",
  cnpsRegistration: "Immatriculation de l'entreprise à la CNPS",
};

const formalityName = (from) => FORMALITY_NAMES[from] || "Formalité";

function FormalityPayment() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const cart = useSelector((state) => state.cart);
  const { formalityRequest } = useSelector((state) => state.formalityRequest);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const totalTTC = cart.price;
  const cardOffer = useCardOffer(
    user?.token,
    formalityRequest?.reference,
    totalTTC,
  );
  const payingByCard = Boolean(cardOffer) && selectedPayment === CARD;

  const phoneSchema = Yup.object({
    phoneNumber: Yup.string().required("Le numéro de téléphone est requis"),
  });

  const formik = useFormik({
    initialValues:
      selectedPayment === "OM" || selectedPayment === "MOMO"
        ? { phoneNumber: "" }
        : {},
    validationSchema:
      selectedPayment === "OM" || selectedPayment === "MOMO"
        ? phoneSchema
        : null,
    onSubmit: async (values) => {
      setSubmitting(true);
      if (selectedPayment === "OM" || selectedPayment === "MOMO") {
        await dispatch(
          mobilePayment(
            user.token,
            {
              request: formalityRequest?.reference,
              phone: values.phoneNumber,
            },
            handleMobilePaymentCallback,
          ),
        );
      } else if (selectedPayment === CARD) {
        await dispatch(setPaymentFrom("formality"));
        await dispatch(
          cardPayment(
            user.token,
            {
              request: formalityRequest?.reference,
            },
            handleCardPaymentCallback,
          ),
        );
      }
    },
  });

  const handleMobilePaymentCallback = async ({ success, message, data }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      await dispatch(
        checkPayment(
          user.token,
          data?.reference,
          formalityRequest?.reference,
          handleCheckPaymentCallback,
        ),
      );
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
      setSubmitting(false);
    }
  };

  const handleCheckPaymentCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
    });
    if (success) {
      navigate("/formality-finalization");
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  const handleCardPaymentCallback = async ({ success, message, data }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      setSubmitting(false);
      openCardCheckout(navigate, data);
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
      setSubmitting(false);
    }
  };

  const handlePaymentSelect = (method) => {
    setSelectedPayment(method);
    formik.resetForm();
  };

  const paymentMethods = [
    { id: "OM", label: "Orange Money", logo: "/images/payment/orange.png" },
    { id: "MOMO", label: "MTN Mobile Money", logo: "/images/payment/mtn.png" },
  ];

  // Visual identity lists each ordered item, other formalities have one line
  const items =
    cart.from === "visualIdentity" && cart.visualIdentity?.items?.length
      ? cart.visualIdentity.items.map((item) => ({
          key: item.key,
          title: item.title,
          detail: item.quantity ? `${item.quantity} unités` : "Paiement unique",
          amount: item.amount,
        }))
      : [
          {
            key: cart.from,
            // A request paid later carries its own title
            title:
              cart.from === "resume" ? cart.title : formalityName(cart.from),
            detail: "Paiement unique",
            amount: totalTTC,
          },
        ];

  return (
    <div className="cart-page">
      <FormalityHeader from={cart.from} />
      <Loader isLoading={submitting} />

      <div className="container cart-container">
        <header className="cart-header">
          <h1 className="cart-title">
            Votre <span>commande</span>
          </h1>
          <p className="cart-lead">
            Vérifiez votre démarche, puis réglez en toute sécurité par Mobile
            Money{cardOffer ? " ou par carte bancaire" : ""}.
          </p>
        </header>

        <div className="cart-layout">
          <section className="cart-items" aria-label="Démarche commandée">
            {items.map((item) => (
              <article key={item.key} className="cart-item">
                <div className="cart-item-main">
                  <h2 className="cart-item-title">{item.title}</h2>
                  <span className="cart-item-period">{item.detail}</span>
                </div>
                <div className="cart-item-side">
                  <strong className="cart-item-price">
                    {formatPrice(item.amount)}
                  </strong>
                </div>
              </article>
            ))}

            {formalityRequest?.reference && (
              <p className="cart-item-detail">
                Référence de la demande : {formalityRequest.reference}
              </p>
            )}
          </section>

          <aside
            className="cart-summary"
            aria-label="Récapitulatif et paiement"
          >
            <h2 className="cart-section-title">Récapitulatif</h2>

            <div className="cart-summary-lines">
              {items.map((item) => (
                <div key={item.key} className="cart-summary-line">
                  <span>{item.title}</span>
                  <span>{formatPrice(item.amount)}</span>
                </div>
              ))}
            </div>

            {payingByCard && cardOffer.fee > 0 && (
              <div className="cart-summary-lines is-fee">
                <div className="cart-summary-line">
                  <span>Frais de paiement par carte</span>
                  <span>{formatPrice(cardOffer.fee)}</span>
                </div>
              </div>
            )}

            <div className="cart-total">
              <span>Total à payer</span>
              <strong>
                {formatPrice(payingByCard ? cardOffer.total : totalTTC)}
              </strong>
            </div>

            <h3 className="cart-pay-title">Moyen de paiement</h3>
            <div
              className="cart-pay-methods"
              role="radiogroup"
              aria-label="Moyen de paiement"
            >
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  role="radio"
                  aria-checked={selectedPayment === method.id}
                  className={`cart-pay-method ${
                    selectedPayment === method.id ? "is-selected" : ""
                  }`}
                  onClick={() => handlePaymentSelect(method.id)}
                >
                  <img src={method.logo} alt="" />
                  <span>{method.label}</span>
                </button>
              ))}
              {cardOffer && (
                <CardPaymentMethod
                  selected={selectedPayment === CARD}
                  onSelect={() => handlePaymentSelect(CARD)}
                />
              )}
            </div>

            <Form onSubmit={formik.handleSubmit} className="cart-pay-form">
              {(selectedPayment === "OM" || selectedPayment === "MOMO") && (
                <Form.Group controlId="phoneNumber" className="mb-3">
                  <Form.Label className="cart-pay-label">
                    Numéro Mobile Money
                  </Form.Label>
                  <PhoneInput
                    country={user.country.toLowerCase() || "cm"}
                    value=""
                    placeholder="Numéro de téléphone"
                    onChange={(phone) => {
                      const formattedPhone = phone.startsWith("+")
                        ? phone
                        : `+${phone}`;
                      formik.setFieldValue("phoneNumber", formattedPhone);
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "46px",
                      fontSize: "15px",
                      borderRadius: "12px",
                    }}
                  />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div className="text-danger mt-1">
                      {formik.errors.phoneNumber}
                    </div>
                  )}
                </Form.Group>
              )}

              <Button
                type="submit"
                className="cart-pay-button"
                disabled={totalTTC <= 0 || !selectedPayment || !formik.isValid}
              >
                <FiLock aria-hidden="true" /> Procéder au paiement
              </Button>

              <p className="cart-fee-note">
                {payingByCard
                  ? "Vous saisirez votre carte sur la page sécurisée de notre partenaire de paiement. Des frais de paiement par carte peuvent s'ajouter au montant total."
                  : "Des frais de transaction de votre opérateur Mobile Money peuvent s'ajouter au montant total."}
              </p>
            </Form>
          </aside>
        </div>
      </div>

      <GuideSection {...paymentGuide} />
    </div>
  );
}

export default FormalityPayment;
